/**
 * AddsecureHeyflowTracker class for managing UTM and GCLID parameters across pageviews.
 *
 * This class handles persisting tracking parameters in sessionStorage and
 * injecting them into Heyflow forms to maintain attribution data.
 */
class AddsecureHeyflowTracker {
	/**
	 * Initialize the AddsecureHeyflowTracker.
	 *
	 * @return {void}
	 */
	constructor() {
		// List of tracking parameters we want to persist
		this.trackingKeys = [
			'utm_source',
			'utm_medium',
			'utm_campaign',
			'utm_term',
			'utm_content',
			'gclid',
		];

		// Initialize and run the sync process
		this.init();
	}

	/**
	 * Initialize the tracker and run the sync if a Heyflow wrapper exists.
	 *
	 * @return {void}
	 */
	init = () => {
		// Always attempt to store tracking parameters in sessionStorage
		this.saveParameters();

		// Find Heyflow wrapper element in the DOM
		const heyflowWrapper = document.querySelector( 'heyflow-wrapper' );

		// Exit early if no Heyflow wrapper is found
		if ( ! heyflowWrapper ) {
			return;
		}

		// Listen to Heyflow wrapper events
		window.addEventListener( 'heyflow-submit', this.onHeyflowSubmit );

		// Process and sync the tracking parameters
		this.syncParameters( heyflowWrapper );
	};

	/**
	 * Handle when the Heyflow form is submitted.
	 */
	onHeyflowSubmit = () => {
		this.removeSessionStorageParams();
	};

	/**
	 * Extract GCLID from Google Ads cookie if available.
	 *
	 * @return {string|null} The GCLID value or null if not found
	 */
	getGclidFromCookie = () => {
		const match = document.cookie.match( /_gcl_aw=GCL\.\d+\.(Cj[A-Za-z0-9\-_]+)/ );
		return match ? match[ 1 ] : null;
	};

	/**
	 * Add all tracking parameters to sessionStorage.
	 */
	saveParameters = () => {
		const url = new URL( window.location.href );

		// Process each tracking parameter
		this.trackingKeys.forEach( key => {
			// Check if parameter exists in current URL
			const value = url.searchParams.get( key );

			if ( value ) {
				// Save parameter from URL to sessionStorage
				sessionStorage.setItem( key, value );
			}
		} );
	};

	/**
	 * Process and synchronize tracking parameters between URL, sessionStorage and Heyflow.
	 *
	 * @param {HTMLElement} heyflowWrapper - The Heyflow wrapper element
	 * @return {void}
	 */
	syncParameters = heyflowWrapper => {
		const url = new URL( window.location.href );
		let changed = false;
		const paramMap = new URLSearchParams();

		// Process each tracking parameter
		this.trackingKeys.forEach( key => {
			// Check if parameter exists in current URL
			let value = url.searchParams.get( key );

			if ( ! value ) {
				// Try to retrieve from sessionStorage if not in URL
				value = sessionStorage.getItem( key );

				// Special case for GCLID: try to recover from cookie if not in sessionStorage
				if ( key === 'gclid' && ! value ) {
					value = this.getGclidFromCookie();
					if ( value ) {
						sessionStorage.setItem( key, value );
					}
				}

				// Add parameter back to URL if found in storage
				if ( value ) {
					url.searchParams.set( key, value );
					changed = true;
				}
			}

			// Add to parameter map for Heyflow if value exists
			if ( value ) {
				paramMap.set( key, value );
			}
		} );

		// Update browser URL if parameters were added
		if ( changed ) {
			window.history.replaceState( {}, '', url );
		}

		// Set the url-parameters attribute on the Heyflow wrapper
		heyflowWrapper.setAttribute( 'url-parameters', paramMap.toString() );
	};

	/**
	 * Remove the URL parameters from the session storage when the heyflow form is submitted.
	 */
	removeSessionStorageParams = () => {
		this.trackingKeys.forEach( key => {
			sessionStorage.removeItem( key );
		} );
	};
}

// Initialize the tracker when the script loads
new AddsecureHeyflowTracker();
